'use client';

import { useState } from 'react';
import { useTimezoneCheck } from '@/hooks/use-timezone-check';
import { useUpdateProfile } from '@/hooks/use-profile';
import { useAuthStore } from '@/hooks/use-auth';
import { getTimezoneDisplayName } from '@/lib/timezone-utils';
import { Button } from '@/components/ui/button';

/**
 * Shows a warning banner when the user's browser timezone differs from their
 * profile timezone. Appears at the top of time-entry pages (availability grid,
 * enrollment preferences). Offers the user two choices:
 *   1. Dismiss — proceed using their profile timezone
 *   2. Update — change their profile timezone to match the browser
 */
export function TimezoneMismatchBanner() {
  const { mismatch, browserTz, profileTz } = useTimezoneCheck();
  const updateProfile = useUpdateProfile();
  const user = useAuthStore((s) => s.user);
  const setAuth = useAuthStore((s) => s.setAuth);
  const accessToken = useAuthStore((s) => s.accessToken);
  const [dismissed, setDismissed] = useState(false);

  if (!mismatch || dismissed) return null;

  const handleUpdate = () => {
    updateProfile.mutate(
      {
        firstName: user!.firstName,
        lastName: user!.lastName,
        timezone: browserTz,
      },
      {
        onSuccess: () => {
          // Update the local auth store so the hook stops showing the banner
          if (accessToken && user) {
            setAuth(accessToken, { ...user, timezone: browserTz });
          }
          setDismissed(true);
        },
      }
    );
  };

  return (
    <div
      role="alert"
      className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-4 flex flex-col sm:flex-row sm:items-center gap-3"
    >
      <div className="flex-1 text-sm text-amber-900">
        Your browser timezone is{' '}
        <strong>{getTimezoneDisplayName(browserTz)}</strong>, but your profile
        is set to <strong>{getTimezoneDisplayName(profileTz)}</strong>. Times
        below are shown in your profile timezone.
      </div>
      <div className="flex gap-2 shrink-0">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setDismissed(true)}
        >
          Keep {getTimezoneDisplayName(profileTz)}
        </Button>
        <Button
          size="sm"
          onClick={handleUpdate}
          disabled={updateProfile.isPending}
        >
          {updateProfile.isPending
            ? 'Updating...'
            : `Switch to ${getTimezoneDisplayName(browserTz)}`}
        </Button>
      </div>
    </div>
  );
}
